import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Sprout, ArrowRight, CreditCard } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { heroImages } from "@/lib/dummyData";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isAdminRole } from "@/lib/navigation";
import { payRegistration } from "@/lib/registrationPayment";

export default function Login() {
  const [show, setShow] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { login, apiError } = useAuth();
  // Tagihan pendaftaran Midtrans yang belum dibayar (akun menunggu pembayaran).
  const [regPayment, setRegPayment] = useState(null);
  const [paying, setPaying] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRegPayment(null);
    try {
      const user = await login(identifier.trim(), password);
      toast.success(`Selamat datang, ${user.name}!`);
      // Arahkan sesuai role: pengurus -> dasbor admin, lainnya -> ruang anggota/penjual.
      setTimeout(() => nav(isAdminRole(user.role) ? "/admin" : "/anggota"), 500);
    } catch (err) {
      toast.error(apiError(err));
      const pay = err?.response?.data?.registration_payment;
      if (pay && pay.status !== "PAID") setRegPayment(pay);
    } finally {
      setLoading(false);
    }
  };

  const payNow = async () => {
    setPaying(true);
    try {
      const res = await payRegistration(regPayment);
      if (res === "PAID") setRegPayment(null);
    } catch (e) {
      toast.error(e.message || "Gagal membuka pembayaran");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] grid lg:grid-cols-2 bg-cream">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img src={heroImages.primary} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-forest/85 via-forest/40 to-transparent" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-cream">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-2xl bg-amber-warm text-ink grid place-items-center"><Sprout size={20} /></span>
            <div className="font-display font-bold text-lg">Koperasi Andalan Dapur Nusantara</div>
          </Link>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
              "Tabungan kecil saya tumbuh — <span className="font-editorial italic text-amber-warm">karena bersama</span>."
            </h2>
            <p className="mt-3 text-cream/85 text-sm max-w-md">Marwan, anggota sejak 2019 · Petani Kopi, Sleman</p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6 animate-fade-up">
          <div>
            <div className="pill-tag bg-forest/10 text-forest mb-3">Ruang Anggota</div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink leading-tight">Selamat datang kembali.</h1>
            <p className="text-ink/75 mt-2 text-sm">Masukkan kredensial Anda untuk masuk ke dasbor.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-ink/55 uppercase tracking-wider">Email atau No. Anggota</Label>
              <Input type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="annisa@example.id atau KOP-2026-000004" className="bg-white border-stone-soft rounded-lg h-11" data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <Label className="text-xs text-ink/55 uppercase tracking-wider">Kata Sandi</Label>
                <button type="button" className="text-xs text-forest font-semibold hover:underline">Lupa sandi?</button>
              </div>
              <div className="relative">
                <Input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-white border-stone-soft rounded-lg h-11 pr-10" data-testid="login-password" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/55">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="remember" defaultChecked />
              <Label htmlFor="remember" className="text-xs text-ink/75 cursor-pointer">Ingat saya di perangkat ini</Label>
            </div>
          </div>

          {regPayment && (
            <div className="rounded-2xl border border-amber-warm/40 bg-amber-warm/10 p-4 text-sm" data-testid="login-registration-payment">
              <div className="font-semibold text-ink">Selesaikan pembayaran pendaftaran</div>
              <p className="text-ink/75 mt-1">
                Akun aktif setelah biaya pendaftaran (Simpanan Pokok + Wajib bulan pertama) dibayar dan data
                diverifikasi Kantor Ranting.
              </p>
              <Button type="button" onClick={payNow} disabled={paying} className="mt-3 rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90 font-semibold" data-testid="login-pay-registration">
                <CreditCard size={16} /> {paying ? "Membuka…" : "Bayar Sekarang"}
              </Button>
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full rounded-full bg-forest hover:bg-forest-dark text-white h-12" data-testid="login-submit">
            {loading ? "Memproses..." : "Masuk"} <ArrowRight size={16} />
          </Button>

          <p className="text-xs text-center text-ink/55">
            Satu pintu untuk anggota & pengurus — Anda otomatis diarahkan sesuai peran.
          </p>

          <p className="text-sm text-center text-ink/75">
            Belum jadi anggota? <Link to="/daftar" className="text-forest font-semibold hover:underline">Daftar online</Link>
          </p>
          <p className="text-sm text-center text-ink/75">
            Ingin menjadi pengurus wilayah? <Link to="/daftar-pengurus" className="text-forest font-semibold hover:underline">Daftar sebagai Admin Wilayah</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
